import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Plus, Loader2, Check } from "lucide-react";
import { decodeEntities } from "@/lib/sanitize";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { getRecentEpisodes, type RecentEpisode } from "@/lib/recentEpisode";
import { useTrendingQuestions } from "@/hooks/useTrendingQuestions";
import EmptyStateShell from "./EmptyStateShell";
import { toast } from "sonner";

interface RecentWithId extends RecentEpisode {
  episode_id?: string | null;
}

/**
 * Empty state for the Saved Episodes tab.
 *
 * Hierarchy:
 *  1. If we have recently viewed episodes in localStorage → show 1-click "Save" buttons.
 *  2. Else → show 3 trending questions as instant search shortcuts.
 */
export default function EmptySavedEpisodes({
  onSaved,
}: {
  onSaved?: (episodeId: string) => void;
}) {
  const { user } = useAuth();
  const [recents, setRecents] = useState<RecentWithId[]>([]);
  const [savingSlug, setSavingSlug] = useState<string | null>(null);
  const [savedSlugs, setSavedSlugs] = useState<Set<string>>(new Set());
  const { questions } = useTrendingQuestions(3);

  useEffect(() => {
    const list = getRecentEpisodes(5);
    if (!list.length) return;
    // Hydrate episode_ids from slug for save action.
    supabase
      .from("episodes")
      .select("id, slug")
      .in("slug", list.map((r) => r.slug))
      .then(({ data }) => {
        const slugToId = new Map((data ?? []).map((r: any) => [r.slug, r.id]));
        setRecents(list.map((r) => ({ ...r, episode_id: slugToId.get(r.slug) ?? null })));
      });
  }, []);

  const handleSave = async (rec: RecentWithId) => {
    if (!user || !rec.episode_id) return;
    setSavingSlug(rec.slug);
    const { error } = await supabase
      .from("saved_episodes")
      .insert({ user_id: user.id, episode_id: rec.episode_id });
    setSavingSlug(null);
    if (error && !error.message.includes("duplicate")) {
      toast.error("Couldn't save — try again");
      return;
    }
    setSavedSlugs((prev) => new Set(prev).add(rec.slug));
    toast.success("Saved!");
    onSaved?.(rec.episode_id);
  };

  return (
    <EmptyStateShell
      icon={Bookmark}
      emoji="📑"
      headline="Your stash is empty!"
      blurb="Save episodes to revisit gold quotes, build playlists for your team, and add timestamped notes. Think of it as your personal commerce playbook."
    >
      {recents.length > 0 ? (
        <div>
          <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
            👀 Save one you just looked at?
          </p>
          <ul className="space-y-2">
            {recents.map((rec) => {
              const isSaved = savedSlugs.has(rec.slug);
              const isSaving = savingSlug === rec.slug;
              return (
                <li
                  key={rec.slug}
                  className="flex items-center gap-2 sm:gap-3 p-1.5 sm:p-2 retro-border bg-background"
                >
                  {rec.artwork_url ? (
                    <img
                      src={rec.artwork_url}
                      alt=""
                      className="w-9 h-9 sm:w-10 sm:h-10 retro-border object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-9 h-9 sm:w-10 sm:h-10 retro-border bg-muted flex items-center justify-center flex-shrink-0">
                      <span className="font-retro text-xs">?</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <Link
                      to={`/episode/${rec.slug}`}
                      className="block font-retro font-bold text-[11px] sm:text-sm leading-tight line-clamp-2 hover:text-primary transition-colors"
                    >
                      {decodeEntities(rec.title)}
                    </Link>
                    <p className="font-mono text-[9px] sm:text-[10px] text-muted-foreground truncate">
                      {rec.podcast_name || "Unknown podcast"}
                    </p>
                  </div>
                  <button
                    onClick={() => handleSave(rec)}
                    disabled={isSaved || isSaving || !rec.episode_id}
                    aria-label={isSaved ? "Saved" : `Save ${rec.title}`}
                    className={`flex-shrink-0 font-mono font-bold text-[10px] sm:text-[11px] px-2 py-1.5 retro-border transition-colors flex items-center gap-1 min-w-[44px] justify-center ${
                      isSaved
                        ? "bg-green-500/20 text-foreground cursor-default"
                        : "bg-primary text-primary-foreground hover:bg-retro-amber active:translate-y-px"
                    }`}
                    title={isSaved ? "Saved" : "Save episode"}
                  >
                    {isSaving ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : isSaved ? (
                      <Check className="w-3 h-3" />
                    ) : (
                      <>
                        <Plus className="w-3 h-3" /> Save
                      </>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <div>
          <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-wider text-muted-foreground font-bold mb-2">
            🔥 Try one to get started
          </p>
          <ul className="space-y-1.5">
            {questions.slice(0, 3).map((q) => (
              <li key={q.text}>
                <Link
                  to={`/?q=${encodeURIComponent(q.text)}`}
                  className="block font-mono text-[11px] sm:text-xs px-3 py-2 retro-border bg-background hover:bg-retro-amber/20 active:translate-y-px transition-colors"
                >
                  → {q.text}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </EmptyStateShell>
  );
}
